export type OrderStatus = 'confirmed' | 'production' | 'quality_check' | 'shipped' | 'arrived';

export interface OrderDocument {
  name: string;
  type: string;
  icon: string;
  available: boolean;
  date?: string;
}

export interface ShipmentInfo {
  vessel: string;
  voyage: string;
  containerNo: string;
  blNumber: string;
  portOfLoading: string;
  portOfDischarge: string;
  etd: string;
  eta: string;
  shippingLine: string;
}

export interface OrderItem {
  product: string;
  spec: string;
  quantity: string;
  weight: string;
}

export interface Order {
  orderNumber: string;
  email: string;
  status: OrderStatus;
  placedDate: string;
  estimatedDelivery: string;
  buyer: string;
  company: string;
  country: string;
  flag: string;
  items: OrderItem[];
  totalWeight: string;
  totalValue: string;
  paymentMethod: string;
  accountManager: string;
  accountManagerPhone: string;
  shipment?: ShipmentInfo;
  documents: OrderDocument[];
  timeline: {
    step: OrderStatus;
    label: string;
    date: string;
    done: boolean;
    note?: string;
  }[];
}

export const mockOrders: Order[] = [
  {
    orderNumber: 'SLG-2026-0312',
    email: 'ahmed@gulfconstruction.ae',
    status: 'shipped',
    placedDate: 'Mar 5, 2026',
    estimatedDelivery: 'Apr 10, 2026',
    buyer: 'Ahmed Al-Rashidi',
    company: 'Gulf Construction LLC',
    country: 'UAE',
    flag: '🇦🇪',
    items: [
      { product: 'H-Beam Steel', spec: 'HW 200×200mm, Q345B, 12m', quantity: '120 pcs', weight: '48 MT' },
      { product: 'ERW Black Pipe', spec: '4" SCH40, ASTM A53, 6m', quantity: '800 pcs', weight: '32 MT' },
    ],
    totalWeight: '80 MT',
    totalValue: 'USD 68,400',
    paymentMethod: 'T/T (30% deposit + 70% before shipment)',
    accountManager: 'Lisa Chen',
    accountManagerPhone: '+86 17671262710',
    shipment: {
      vessel: 'COSCO SHIPPING UNIVERSE',
      voyage: 'V.2026-08E',
      containerNo: 'CSNU7234561 / CSNU7234562',
      blNumber: 'COSU6234891200',
      portOfLoading: 'Shanghai, China (CNSHA)',
      portOfDischarge: 'Jebel Ali, UAE (AEJEA)',
      etd: 'Mar 18, 2026',
      eta: 'Apr 8, 2026',
      shippingLine: 'COSCO Shipping Lines',
    },
    documents: [
      { name: 'Commercial Invoice', type: 'PDF', icon: 'ri-file-text-line', available: true, date: 'Mar 15, 2026' },
      { name: 'Packing List', type: 'PDF', icon: 'ri-file-list-3-line', available: true, date: 'Mar 15, 2026' },
      { name: 'Bill of Lading (B/L)', type: 'PDF', icon: 'ri-ship-line', available: true, date: 'Mar 19, 2026' },
      { name: 'Mill Test Certificate (MTC)', type: 'PDF', icon: 'ri-award-line', available: true, date: 'Mar 12, 2026' },
      { name: 'Certificate of Origin (COO)', type: 'PDF', icon: 'ri-global-line', available: true, date: 'Mar 17, 2026' },
      { name: 'SGS Inspection Report', type: 'PDF', icon: 'ri-shield-check-line', available: true, date: 'Mar 14, 2026' },
    ],
    timeline: [
      { step: 'confirmed', label: 'Order Confirmed', date: 'Mar 5, 2026', done: true, note: 'Deposit received. Sales contract signed.' },
      { step: 'production', label: 'In Production', date: 'Mar 8, 2026', done: true, note: 'Mill order placed with Baosteel. Production started.' },
      { step: 'quality_check', label: 'Quality Inspection', date: 'Mar 14, 2026', done: true, note: 'SGS inspection passed. MTC issued.' },
      { step: 'shipped', label: 'Shipped', date: 'Mar 18, 2026', done: true, note: 'Loaded on COSCO SHIPPING UNIVERSE. B/L issued.' },
      { step: 'arrived', label: 'Arrived at Port', date: 'Apr 8, 2026 (ETA)', done: false, note: 'Estimated arrival at Jebel Ali Port.' },
    ],
  },
  {
    orderNumber: 'SLG-2026-0287',
    email: 'emmanuel@okaforsteel.ng',
    status: 'production',
    placedDate: 'Feb 28, 2026',
    estimatedDelivery: 'Apr 25, 2026',
    buyer: 'Emmanuel Okafor',
    company: 'Okafor Steel Merchants',
    country: 'Nigeria',
    flag: '🇳🇬',
    items: [
      { product: 'Rebar HRB400', spec: '16mm dia, HRB400, 12m', quantity: '500 bundles', weight: '120 MT' },
      { product: 'ERW Galvanized Pipe', spec: '2" SCH40, BS1387, 6m', quantity: '600 pcs', weight: '18 MT' },
    ],
    totalWeight: '138 MT',
    totalValue: 'USD 94,200',
    paymentMethod: 'L/C at sight (HSBC Nigeria)',
    accountManager: 'David Wang',
    accountManagerPhone: '+86 17671262710',
    documents: [
      { name: 'Commercial Invoice', type: 'PDF', icon: 'ri-file-text-line', available: true, date: 'Mar 1, 2026' },
      { name: 'Packing List', type: 'PDF', icon: 'ri-file-list-3-line', available: false },
      { name: 'Bill of Lading (B/L)', type: 'PDF', icon: 'ri-ship-line', available: false },
      { name: 'Mill Test Certificate (MTC)', type: 'PDF', icon: 'ri-award-line', available: false },
      { name: 'Certificate of Origin (COO)', type: 'PDF', icon: 'ri-global-line', available: false },
      { name: 'SGS Inspection Report', type: 'PDF', icon: 'ri-shield-check-line', available: false },
    ],
    timeline: [
      { step: 'confirmed', label: 'Order Confirmed', date: 'Feb 28, 2026', done: true, note: 'L/C received and verified. Order confirmed.' },
      { step: 'production', label: 'In Production', date: 'Mar 3, 2026', done: true, note: 'Mill production in progress. Est. completion Mar 28.' },
      { step: 'quality_check', label: 'Quality Inspection', date: 'Mar 30, 2026 (est.)', done: false, note: 'SGS inspection scheduled.' },
      { step: 'shipped', label: 'Shipped', date: 'Apr 5, 2026 (est.)', done: false },
      { step: 'arrived', label: 'Arrived at Port', date: 'Apr 25, 2026 (est.)', done: false, note: 'Estimated arrival at Apapa Port, Lagos.' },
    ],
  },
  {
    orderNumber: 'SLG-2026-0341',
    email: 'rajesh@patelbuildingmaterials.in',
    status: 'arrived',
    placedDate: 'Jan 20, 2026',
    estimatedDelivery: 'Mar 15, 2026',
    buyer: 'Rajesh Patel',
    company: 'Patel Building Materials',
    country: 'India',
    flag: '🇮🇳',
    items: [
      { product: 'Cold-Rolled Coil (CRC)', spec: 'SPCC, 0.5mm×1250mm, DC01', quantity: '20 coils', weight: '60 MT' },
    ],
    totalWeight: '60 MT',
    totalValue: 'USD 42,000',
    paymentMethod: 'T/T (30% deposit + 70% before shipment)',
    accountManager: 'Lisa Chen',
    accountManagerPhone: '+86 17671262710',
    shipment: {
      vessel: 'MSC ANNA',
      voyage: 'V.AE6-2026',
      containerNo: 'MSCU4512378',
      blNumber: 'MSCUSHA123456',
      portOfLoading: 'Ningbo, China (CNNGB)',
      portOfDischarge: 'Nhava Sheva (JNPT), India',
      etd: 'Feb 10, 2026',
      eta: 'Feb 28, 2026',
      shippingLine: 'MSC Mediterranean Shipping',
    },
    documents: [
      { name: 'Commercial Invoice', type: 'PDF', icon: 'ri-file-text-line', available: true, date: 'Feb 8, 2026' },
      { name: 'Packing List', type: 'PDF', icon: 'ri-file-list-3-line', available: true, date: 'Feb 8, 2026' },
      { name: 'Bill of Lading (B/L)', type: 'PDF', icon: 'ri-ship-line', available: true, date: 'Feb 11, 2026' },
      { name: 'Mill Test Certificate (MTC)', type: 'PDF', icon: 'ri-award-line', available: true, date: 'Feb 6, 2026' },
      { name: 'Certificate of Origin (COO)', type: 'PDF', icon: 'ri-global-line', available: true, date: 'Feb 9, 2026' },
      { name: 'SGS Inspection Report', type: 'PDF', icon: 'ri-shield-check-line', available: true, date: 'Feb 7, 2026' },
    ],
    timeline: [
      { step: 'confirmed', label: 'Order Confirmed', date: 'Jan 20, 2026', done: true, note: 'Deposit received. Order confirmed.' },
      { step: 'production', label: 'In Production', date: 'Jan 23, 2026', done: true, note: 'Mill production completed ahead of schedule.' },
      { step: 'quality_check', label: 'Quality Inspection', date: 'Feb 6, 2026', done: true, note: 'SGS inspection passed. All coils within spec.' },
      { step: 'shipped', label: 'Shipped', date: 'Feb 10, 2026', done: true, note: 'Loaded on MSC ANNA at Ningbo Port.' },
      { step: 'arrived', label: 'Arrived at Port', date: 'Mar 1, 2026', done: true, note: 'Arrived at JNPT. Customs clearance completed.' },
    ],
  },
];
